import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Option, Field } from '@fluentui/react-components';
import { DatePicker, BasicButton } from '@/components/common';
import { useGetBuildingsQuery } from '@store/api/spaceConfigService';
import type { MetricsPeriodInput } from '@store/models';
import type { MetricsPeriodSlot } from '@/hooks/usePlannerMetrics';
import { useMetricsFilterBarStyles } from './MetricsFilterBar.styles';

/** Sentinel option values for the in-dropdown bulk-selection actions. */
const SELECT_ALL_OPTION = '__select_all__';
const CLEAR_ALL_OPTION = '__clear_all__';

/** Props for {@link MetricsFilterBar}. */
export interface MetricsFilterBarProps {
  /** Selected building filter (empty means all buildings). */
  buildingIds: string[];
  /** Called when the building selection changes. */
  onBuildingIdsChange: (ids: string[]) => void;
  /** Active periods (1 primary, optionally a 2nd comparison period). */
  periods: MetricsPeriodInput[];
  /** Called when a period at the given slot changes. */
  onPeriodChange: (slot: MetricsPeriodSlot, period: MetricsPeriodInput) => void;
  /** True when a comparison period is active. */
  hasComparison: boolean;
  /** Adds a second comparison period. */
  onAddComparison: () => void;
  /** Removes the comparison period. */
  onRemoveComparison: () => void;
}

/** Converts a Date to a local YYYY-MM-DD string. */
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parses a YYYY-MM-DD string to a local Date, or undefined when empty. */
function fromIsoDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

/**
 * Filter controls for the planner metrics report: a building multi-select and
 * one or two date-range pickers. Supports adding/removing an optional second
 * comparison period (US3). Selection is lifted to `usePlannerMetrics`.
 */
export const MetricsFilterBar = ({
  buildingIds,
  onBuildingIdsChange,
  periods,
  onPeriodChange,
  hasComparison,
  onAddComparison,
  onRemoveComparison,
}: MetricsFilterBarProps) => {
  const styles = useMetricsFilterBarStyles();
  const { t } = useTranslation('metricsReport');
  const { data: buildingsResponse } = useGetBuildingsQuery();

  const buildings = useMemo(
    () => (buildingsResponse?.success ? buildingsResponse.data : []),
    [buildingsResponse]
  );
  const buildingNameById = useMemo(() => {
    const map = new Map<string, string>();
    buildings.forEach((b) => map.set(b.buildingId, b.buildingName));
    return map;
  }, [buildings]);

  const buildingSelectionText =
    buildingIds.length === 0
      ? t('filters.allBuildings')
      : buildingIds.map((id) => buildingNameById.get(id) ?? id).join(', ');

  const allBuildingIds = useMemo(() => buildings.map((b) => b.buildingId), [buildings]);

  const handleOptionSelect = (_e: unknown, data: { selectedOptions: string[] }) => {
    if (data.selectedOptions.includes(CLEAR_ALL_OPTION)) {
      onBuildingIdsChange([]);
      return;
    }
    if (data.selectedOptions.includes(SELECT_ALL_OPTION)) {
      onBuildingIdsChange(allBuildingIds);
      return;
    }
    onBuildingIdsChange(
      data.selectedOptions.filter((id) => id !== SELECT_ALL_OPTION && id !== CLEAR_ALL_OPTION)
    );
  };

  const renderPeriod = (slot: MetricsPeriodSlot, labelKey: string) => {
    const period = periods[slot] ?? { startDate: '', endDate: '' };
    return (
      <div className={styles.periodGroup}>
        <span className={styles.periodLabel}>{t(labelKey)}</span>
        <div className={styles.dateRow}>
          <DatePicker
            label={t('filters.startDatePlaceholder')}
            value={fromIsoDate(period.startDate) ?? null}
            maxDate={fromIsoDate(period.endDate)}
            onSelectDate={(date) =>
              onPeriodChange(slot, { ...period, startDate: date ? toIsoDate(date) : '' })
            }
          />
          <DatePicker
            label={t('filters.endDatePlaceholder')}
            value={fromIsoDate(period.endDate) ?? null}
            minDate={fromIsoDate(period.startDate)}
            onSelectDate={(date) =>
              onPeriodChange(slot, { ...period, endDate: date ? toIsoDate(date) : '' })
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.root} role="group" aria-label={t('title')}>
      <Field className={styles.field} label={t('filters.buildingsLabel')}>
        <Dropdown
          multiselect
          appearance="outline"
          size="large"
          placeholder={t('filters.buildingsPlaceholder')}
          selectedOptions={buildingIds}
          value={buildingSelectionText}
          onOptionSelect={handleOptionSelect}
          data-testid="metrics-buildings-dropdown"
        >
          <Option
            key={SELECT_ALL_OPTION}
            value={SELECT_ALL_OPTION}
            text={t('filters.selectAll')}
            disabled={allBuildingIds.length === 0 || buildingIds.length === allBuildingIds.length}
            data-testid="metrics-buildings-select-all"
          >
            {t('filters.selectAll')}
          </Option>
          <Option
            key={CLEAR_ALL_OPTION}
            value={CLEAR_ALL_OPTION}
            text={t('filters.clearAll')}
            disabled={buildingIds.length === 0}
            data-testid="metrics-buildings-clear-all"
          >
            {t('filters.clearAll')}
          </Option>
          {buildings.map((building) => (
            <Option key={building.buildingId} value={building.buildingId}>
              {building.buildingName}
            </Option>
          ))}
        </Dropdown>
      </Field>

      {renderPeriod(0, 'filters.primaryPeriodLabel')}

      {hasComparison ? renderPeriod(1, 'filters.comparisonPeriodLabel') : null}

      <div className={styles.actions}>
        {hasComparison ? (
          <BasicButton
            variant="secondary"
            size="medium"
            onClick={onRemoveComparison}
            data-testid="metrics-remove-comparison"
          >
            {t('filters.removeComparison')}
          </BasicButton>
        ) : (
          <BasicButton
            variant="secondary"
            size="medium"
            onClick={onAddComparison}
            data-testid="metrics-add-comparison"
          >
            {t('filters.addComparison')}
          </BasicButton>
        )}
      </div>
    </div>
  );
};

MetricsFilterBar.displayName = 'MetricsFilterBar';
